import React, { useEffect, useState } from "react";
import { uploadAdherentsFile } from "../../../services/adherentServices";
import { getDojosByClub } from "../../../services/dojoServices";
import { getGroupsByDojo } from "../../../services/groupServices";
import { useNotification } from "../../../components/Notification/NotificationManager";
import {
  FaDownload,
  FaFileUpload,
  FaSave,
  FaTimes,
} from "react-icons/fa";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const UploadAdherentsModal = ({
  isOpen,
  onClose,
  onUpdate,
  clubId,
  demoMode = false,
}) => {
  const { addNotification } = useNotification();

  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [dojos, setDojos] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedDojo, setSelectedDojo] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!isOpen || !clubId) return;

    fetchDojos();
    setSelectedDojo("");
    setSelectedGroup("");
    setSelectedFile(null);
    setGroups([]);
    setErrorMessage("");
  }, [isOpen, clubId]);

  const fetchDojos = async () => {
    try {
      const data = await getDojosByClub(clubId);
      setDojos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors du chargement des lieux :", error);
      setDojos([]);
    }
  };

  const fetchGroups = async (dojoId) => {
    try {
      const data = await getGroupsByDojo(dojoId);
      setGroups(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors du chargement des groupes :", error);
      setGroups([]);
    }
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (demoMode) {
      setErrorMessage("Mode Démo : l’importation est désactivée.");
      return;
    }

    if (!selectedFile) {
      setErrorMessage("Veuillez sélectionner un fichier.");
      return;
    }

    if (!selectedGroup) {
      setErrorMessage("Veuillez sélectionner un groupe.");
      return;
    }

    setIsUploading(true);

    try {
      await uploadAdherentsFile(selectedGroup, selectedFile);
      addNotification("Importation réussie !", "success");
      onUpdate();
      onClose();
    } catch (error) {
      console.error("Erreur lors de l'importation :", error);
      addNotification("Erreur lors de l'importation.", "error");
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
      <div className="w-full max-w-5xl overflow-hidden rounded-[28px] border border-white/10 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
        <div className="grid lg:grid-cols-[0.92fr_1.08fr]">
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 p-6 text-white sm:p-8">
            <p className="inline-flex rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
              Importation
            </p>

            <h2 className="mt-4 text-3xl font-black leading-tight">
              Importer des adhérents
            </h2>

            <p className="mt-3 text-sm leading-7 text-white/80">
              Associez le fichier à un groupe puis importez en une seule fois vos
              adhérents.
            </p>

            <div className="mt-8 rounded-[24px] border border-white/10 bg-white/10 p-5">
              <p className="text-sm font-semibold text-white/90">
                Format accepté
              </p>
              <p className="mt-2 text-sm text-white/75">
                .xlsx, .xls, .csv avec entête.
              </p>

              <ul className="mt-4 space-y-2 text-sm text-white/80">
                <li>• nom</li>
                <li>• prenom</li>
                <li>• email</li>
                <li>• tel</li>
                <li>• adresse</li>
                <li>• cp</li>
                <li>• commune</li>
                <li>• genre</li>
                <li>• statut_inscription</li>
                <li>• notes</li>
                <li>• photo_url</li>
                <li>• date_naissance</li>
              </ul>

              <a
                href="/doc/modele_import_adherents.xlsx"
                download
                className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-white px-4 py-3 font-semibold text-slate-900 transition hover:bg-white/90"
              >
                <FaDownload />
                Télécharger le modèle
              </a>
            </div>
          </div>

          <div className="max-h-[90vh] overflow-y-auto p-5 sm:p-6">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                  Import
                </p>
                <h3 className="text-xl font-black text-slate-900 dark:text-slate-100">
                  Sélection du fichier
                </h3>
              </div>

              <button
                onClick={onClose}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                <FaTimes />
              </button>
            </div>

            {errorMessage && (
              <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
                {errorMessage}
              </div>
            )}

            <form onSubmit={handleUploadSubmit} className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                  Lieu
                </label>
                <select
                  value={selectedDojo}
                  onChange={(e) => {
                    setSelectedDojo(e.target.value);
                    setSelectedGroup("");
                    fetchGroups(e.target.value);
                  }}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-emerald-400 dark:focus:ring-emerald-500/20"
                >
                  <option value="">Sélectionner un lieu</option>
                  {dojos.map((dojo) => (
                    <option key={dojo.id} value={dojo.id}>
                      {dojo.nom_dojo}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                  Groupe
                </label>
                <select
                  value={selectedGroup}
                  onChange={(e) => setSelectedGroup(e.target.value)}
                  disabled={!selectedDojo}
                  className={cn(
                    "w-full rounded-2xl border px-4 py-3 text-sm outline-none transition",
                    !selectedDojo
                      ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-500"
                      : "border-slate-200 bg-white text-slate-900 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-emerald-400 dark:focus:ring-emerald-500/20"
                  )}
                >
                  <option value="">Sélectionner un groupe</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.nom_groupe}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                  Fichier
                </label>
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/50">
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    className="block w-full text-sm text-slate-700 file:mr-4 file:rounded-xl file:border-0 file:bg-emerald-600 file:px-4 file:py-2 file:font-semibold file:text-white hover:file:bg-emerald-700 dark:text-slate-200 dark:file:bg-emerald-500 dark:hover:file:bg-emerald-600"
                  />

                  {selectedFile && (
                    <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
                      Fichier sélectionné :{" "}
                      <span className="font-semibold">{selectedFile.name}</span>
                    </p>
                  )}
                </div>
              </div>

              <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-4 text-sm text-blue-700 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300">
                Un fichier par groupe. Vérifiez bien l’ordre et les noms des
                colonnes avant importation.
              </div>

              <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-5 py-3 font-semibold text-slate-800 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                >
                  <FaTimes />
                  Annuler
                </button>

                <button
                  type="submit"
                  disabled={!selectedGroup || !selectedFile || isUploading || demoMode}
                  className={cn(
                    "inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 font-semibold text-white transition",
                    !selectedGroup || !selectedFile || isUploading || demoMode
                      ? "cursor-not-allowed bg-slate-400 dark:bg-slate-700"
                      : "bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600"
                  )}
                >
                  <FaFileUpload />
                  {isUploading ? "Importation..." : "Importer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadAdherentsModal;